import { useEffect, useMemo, useState } from "react";
import styles from "../../../styles/SubscribeCart.module.scss";
import inputStyles from "../../../styles/InformationsForm.module.scss";
import { getStripeProductFromDiseases, applyDiscount } from "../../../utils/liberals";
import { pluralize } from "../../../utils/utils";
import { LoadableText } from "../../UI/LoadableText";
import { STRIPE_PRODUCTS_LS_ID, STRIPE_SELECTED_PRODUCT_LS_ID, DISEASE_LS_ID, STRIPE_INFO } from "../constants";
import useLocalStorage from "../../../hooks/useLocalStorage";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCircleNotch, faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";

const defaultProduct = {
  id: null,
  name: "-",
  localized_price: "€ -,--",
  localized_price_subtaxes: "€ -,--",
  localized_price_taxes: "€ -,--",
  NbThematics: 0,
  NbPatients: 0,
};

/**
 * Root component of the `/subscribe/liberal` route, @ step 2 "Cart"
 *
 * @param {Function} nextStep - The function to call when the user wants to go to the next step
 * @param {Function} previousStep - The function to call when the user wants to go to the previous step
 */
export default function SubscribeCart({ nextStep = () => {}, previousStep = () => {} }) {
  const [localStorageProducts] = useLocalStorage(STRIPE_PRODUCTS_LS_ID, []);
  const [localStorageDiseases] = useLocalStorage(DISEASE_LS_ID, []);
  const [stripeInfo, setStripeInfo] = useLocalStorage(STRIPE_INFO, {});
  const [discountData, setDiscountData] = useState({ value: "", isLoading: false, isValid: false, error: null });

  /** Currently selected product, from the infos stored in the local storage.
   * If no thematic selected (cart is empty), default product is returned. */
  const selectedProduct = useMemo(
    () => (localStorageDiseases.length > 0 ? getStripeProductFromDiseases(localStorageProducts, localStorageDiseases) : defaultProduct),
    [localStorageProducts, localStorageDiseases]
  );

  /** Formatted selected product (with JSX). Use this because setLocalStorageSelectedProduct cannot store JSX.  */
  const displayedSelectedProduct = applyDiscount(selectedProduct, stripeInfo?.discount);

  // Save the selected product in the local storage when calculated
  const [localStorageSelectedProduct, setLocalStorageSelectedProduct] = useLocalStorage(STRIPE_SELECTED_PRODUCT_LS_ID, {});
  useEffect(() => selectedProduct?.id && setLocalStorageSelectedProduct(selectedProduct), [selectedProduct]);

  // You can checkout when the local storage product has been saved
  const isCheckoutReady = localStorageSelectedProduct?.id === selectedProduct?.id;

  // Check if the features list should be displayed (or display a skeleton)
  const featuresListTrigger = !isNaN(selectedProduct.NbThematics) && !isNaN(selectedProduct.NbPatients);

  /** Check if the promo code is valid. */
  const checkPromoCode = async () => {
    if (!discountData.value || discountData.isLoading) return false;
    setDiscountData({ ...discountData, isLoading: true, error: null });
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_NUVEE_BASE_URL}/stripe/${selectedProduct.id}/${discountData.value}`);
      if (data) {
        setStripeInfo({ ...stripeInfo, discount: data });
        setDiscountData({ ...discountData, isLoading: false, isValid: true, error: null });
        return true;
      } else {
        throw new Error();
      }
    } catch (error) {
      setDiscountData({ ...discountData, isLoading: false, isValid: false, error: "Code de réduction invalide" });
      return false;
    }
  };

  /** Handle checkout process with automatic discount code validation */
  const handleCheckout = async () => {
    if (discountData.value && !discountData.isValid && !discountData.error && !discountData.isLoading) {
      const isValid = await checkPromoCode();
      if (isValid) {
        nextStep();
      }
      // Otherwise, the error is displayed and we do not continue
    } else if (discountData.value && discountData.error) {
      // Error already present, do not continue
      return;
    } else {
      // No code or already validated
      nextStep();
    }
  };

  useEffect(() => {
    // Reset Stripe code if it changes
    if (stripeInfo?.discount?.discount_code && discountData.value && discountData.value !== stripeInfo.discount.discount_code) {
      setStripeInfo({ ...stripeInfo, discount: undefined });
    }
    // If the discount code is already in the local storage (when stepping back), set it in the state
    if (stripeInfo?.discount?.discount_code && !discountData.value) {
      setDiscountData({ ...discountData, value: stripeInfo.discount.discount_code, isValid: true });
    }
  }, [discountData.value]);

  return (
    <>
      <div className={styles.cart}>
        <div className={`${styles.choiceCard} ${!selectedProduct.id && styles.fitHeight}`}>
          <h2>Mon choix</h2>
          {selectedProduct.id ? (
            <>
              <div className={styles.content}>
                <div className={styles.offerImage}>
                  <img src={`/images/liberals/liberal_offer_${displayedSelectedProduct.NbThematics}.png`} alt={"Offre"} />
                </div>
                <div className={styles.cartRecap}>
                  <div className={styles.price}>
                    <p className={styles.titleText}>{<LoadableText text={displayedSelectedProduct?.name} />}</p>
                    <p className={styles.priceText}>{<LoadableText text={displayedSelectedProduct?.localized_price} />}</p>
                    <p className={styles.subtitleText}>(paiement mensuel)</p>
                  </div>

                  <ul className={styles.features}>
                    <li>
                      <LoadableText
                        text={`${displayedSelectedProduct.NbThematics} ${pluralize("thématique", displayedSelectedProduct.NbThematics)} Nuvee au choix`}
                        trigger={featuresListTrigger}
                      />
                    </li>
                    <li>
                      <LoadableText text={`${displayedSelectedProduct.NbPatients} accès patients crédités par mois`} trigger={featuresListTrigger} />
                    </li>
                    <li>
                      <LoadableText text={`Sans engagement`} trigger={featuresListTrigger} />
                    </li>
                  </ul>
                  <div className={styles.thematics}>
                    <h2>
                      {pluralize("Thématique", localStorageDiseases.length)} {pluralize("choisie", localStorageDiseases.length)}:
                    </h2>
                    {localStorageDiseases.map(
                      (thematic) =>
                        thematic && (
                          <p key={thematic.id} style={{ color: "#145A79" }}>
                            <LoadableText text={thematic.name} />
                          </p>
                        )
                    )}
                  </div>
                </div>
              </div>
              <button className={styles.previousStep} onClick={previousStep}>
                {"<<"} J'ai changé d'avis
              </button>
            </>
          ) : (
            // If no product, display an empty cart
            <>
              <div className={`${styles.content} ${styles.emptyCart}`}>
                <div className={styles.offerImage}>
                  <img src={`/images/liberals/liberal_no_offer.png`} alt={"Offre"} />
                </div>
                <div className={styles.noProductTexts}>
                  <h2>Oops ! Votre panier semble vide...</h2>
                  <p>N'attendez plus, choisissez une thématique !</p>
                  <button className={styles.nuveeButton} onClick={previousStep}>
                    Choisir une thématique
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles.priceCard} id="cart-section">
          <h2>Détails</h2>
          <div className={styles.prices}>
            <div className={styles.priceLine}>
              <p>
                Montant <span className={styles.subtext}> HT</span>
              </p>
              <p className={styles.totalPrice}>
                <LoadableText text={displayedSelectedProduct?.localized_price_subtaxes} />
              </p>
            </div>
            <div className={styles.priceLine}>
              <p>
                TVA <span className={styles.subtext}> 20%</span>
              </p>
              <p className={styles.totalPrice}>
                <LoadableText text={displayedSelectedProduct?.localized_price_taxes} />
              </p>
            </div>
            <div className={styles.priceLine}>
              <p>
                Total <span className={styles.subtext}> T.T.C</span>
              </p>
              <p className={styles.totalPrice}>
                <LoadableText text={displayedSelectedProduct?.localized_price} />
              </p>
            </div>
            <form className={`${styles.discountInput} ${inputStyles.informationsForm}`} onSubmit={() => {}}>
              <div className={inputStyles.group}>
                <div className={inputStyles.field}>
                  <div className={inputStyles.inputContainer}>
                    <input
                      type="text"
                      id="discount_code"
                      name="discount_code"
                      value={discountData.value}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                          if (discountData.value && !discountData.isLoading && !discountData.isValid && !discountData.error) {
                            checkPromoCode();
                          }
                        }
                      }}
                      onChange={(e) => setDiscountData({ ...discountData, value: e.target.value, error: null, isValid: false })}
                      placeholder="Remplir ici"
                      className={`${inputStyles.input} ${discountData.value ? inputStyles.filled : ""} ${discountData.error ? inputStyles.errorInput : ""}`}
                    />
                    <label htmlFor="discount_code" className={`${inputStyles.label} ${discountData.error ? inputStyles.errorLabel : ""}`}>
                      Code de réduction
                    </label>
                    <div
                      className={styles.discountStatus}
                      onClick={async () => {
                        if (!discountData.value || discountData.isLoading || discountData.isValid) {
                          return;
                        } else if (discountData.error) {
                          setDiscountData({ ...discountData, value: '', error: null });
                        } else {
                          await checkPromoCode();
                        }
                      }}
                    >
                      {discountData.value && !discountData.isLoading && !discountData.isValid && !discountData.error && <FontAwesomeIcon icon={faSearch} />}
                      {discountData.isLoading && <FontAwesomeIcon icon={faCircleNotch} spin />}
                      {discountData.isValid && <FontAwesomeIcon icon={faCheck} color="#4BB543" />}
                      {discountData.error && <FontAwesomeIcon icon={faTimes} color="#e73439" />}
                    </div>
                  </div>
                  {discountData.error && <span className={inputStyles.errorMessage}>{discountData.error}</span>}
                </div>
              </div>
            </form>
          </div>
          <button 
            disabled={!isCheckoutReady || discountData.isLoading} 
            onClick={() => isCheckoutReady && handleCheckout()} 
            className={`${styles.checkout} ${styles.nuveeButton}`}
          >
            {discountData.isLoading ? "Vérification..." : "Procéder au paiement"}
          </button>
          <div className={styles.paymentMethods}>
            <p>Nous acceptons :</p>
            <div className={styles.paymentLogos}>
              <img src="/images/liberals/payment/cb.svg" alt="CB" />
              <img src="/images/liberals/payment/sepa.png" alt="SEPA" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
